import { useState, useEffect } from 'react';
import { Tooltip, IconButton, Switch, FormControlLabel } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { TimeField } from '@mui/x-date-pickers/TimeField';
import TextField from '@mui/material/TextField';
import axios from 'axios';
import { cleanCustomerId } from '../../Services/app/operatorservice';
import '../../Pages/Machines/machine.css';
import { customerbasedshift, shiftadd, Deviceattributeget } from '../../Services/app/masterservice';
import { customerbaseddevices } from '../../Services/app/andondashboardservice';
import { Downtimeadd } from '../../Services/app/masterservice';

import Swal from 'sweetalert2';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

import 'react-time-picker/dist/TimePicker.css';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Button from '@mui/material/Button';
import { MobileTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { DemoItem } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

// Default machine graphic shown when a machine has no photo, or its photo URL fails to load
function DefaultMachineImg() {
  return (
    <svg viewBox="0 0 160 120" width="120" height="92" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="machine">
      <rect x="16" y="86" width="128" height="12" rx="2" fill="#cbd5e1" />
      <rect x="28" y="28" width="74" height="60" rx="4" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="2" />
      <rect x="38" y="40" width="54" height="30" rx="2" fill="#f8fafc" stroke="#94a3b8" strokeWidth="1.5" />
      <rect x="102" y="40" width="32" height="48" rx="3" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="2" />
      <circle cx="118" cy="53" r="4" fill="#94a3b8" />
      <rect x="110" y="64" width="16" height="4" rx="2" fill="#94a3b8" />
      <rect x="110" y="72" width="16" height="4" rx="2" fill="#94a3b8" />
      <rect x="60" y="18" width="6" height="12" fill="#94a3b8" />
      <text x="80" y="112" fontSize="10" fill="#94a3b8" textAnchor="middle" fontFamily="sans-serif">No photo</text>
    </svg>
  );
}

function MachineImage({ src, alt }) {
  const [err, setErr] = useState(false);
  if (!src || err) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.95 }}>
        <DefaultMachineImg />
      </div>
    );
  }
  return <img src={src} alt={alt} onError={() => setErr(true)} style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 8 }} />;
}

// Function to convert seconds to HH:mm:ss format
const formatSecondsToTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return 'Not set';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const MachineList = () => {
    // Dialog state
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editDialogData, setEditDialogData] = useState(null);
    const [dialogOpenCount, setDialogOpenCount] = useState(0);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedDeviceId, setSelectedDeviceId] = useState(null);
    const [timeValue, setTimeValue] = useState(dayjs('00:01:00', 'HH:mm:ss'));
    const customerId = localStorage.getItem('CustomerID');
    const [devices, setDevices] = useState([]);
    const [deviceThresholds, setDeviceThresholds] = useState({});
    const [machineSpecsMap, setMachineSpecsMap] = useState({});
    const [editSpecs, setEditSpecs] = useState({
        image_url: '', manufacturer: '', model: '', year: '',
        max_rpm: '', spindle_power_kw: '', axis_count: '', machine_hour_rate: '',
        rapid_rate: '', axis_motor_torque: '', spindle_motor_torque: '', spindle_speed_curve: '',
    });
    const [openRoboDialog, setOpenRoboDialog] = useState(false);
    const [roboCheckValues, setRoboCheckValues] = useState({});
    const [roboCheckSelected, setRoboCheckSelected] = useState('');
    const [roboDeviceId, setRoboDeviceId] = useState(null);
    const cleanedCustomerID = (id) => (id || '').trim().replace(/^"|"$/g, '').toLowerCase();
    const showRoboCheck = cleanedCustomerID(window._env_.GPLAST_CUSTOMER_ID) === cleanedCustomerID(customerId);

    // Fetch machine_specs for all devices once devices are loaded
    useEffect(() => {
        if (!devices || devices.length === 0) return;
        const base = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');
        const fetchAll = async () => {
            const out = {};
            for (const d of devices) {
                const did = d.id?.id || d.id?.$oid || d.id;
                if (!did) continue;
                try {
                    const r = await axios.get(`${base}/api/operator-assignment/machine-specs?device_id=${did}`);
                    out[did] = r.data || {};
                } catch (e) { out[did] = {}; }
            }
            setMachineSpecsMap(out);
        };
        fetchAll();
    }, [devices]);

    const handleOpenEditDialog = async (rowData) => {
        const itemId = rowData.id?.$oid || rowData.id;
        const key = 'downtime_threasold';
        let defaultTime = dayjs('00:01:00', 'HH:mm:ss');
        // Pre-load current specs into the form so existing values appear
        const did = itemId?.id || itemId;
        const cur = machineSpecsMap[did] || {};
        setEditSpecs({
            image_url: cur.image_url || '',
            manufacturer: cur.manufacturer || '',
            model: cur.model || '',
            year: cur.year || '',
            max_rpm: cur.max_rpm != null ? String(cur.max_rpm) : '',
            spindle_power_kw: cur.spindle_power_kw != null ? String(cur.spindle_power_kw) : '',
            axis_count: cur.axis_count != null ? String(cur.axis_count) : '',
            machine_hour_rate: cur.machine_hour_rate != null ? String(cur.machine_hour_rate) : '',
            rapid_rate: cur.rapid_rate != null ? String(cur.rapid_rate) : '',
            axis_motor_torque: cur.axis_motor_torque != null ? String(cur.axis_motor_torque) : '',
            spindle_motor_torque: cur.spindle_motor_torque != null ? String(cur.spindle_motor_torque) : '',
            spindle_speed_curve: cur.spindle_speed_curve || '',
        });
    
        try {
            const response = await Deviceattributeget(itemId.id, key);
            if (response?.length > 0) {
                const seconds = parseInt(response[0].value, 10) || 60;
                const timeStr = formatSecondsToTime(seconds);
                defaultTime = dayjs(timeStr, 'HH:mm:ss');
                if (defaultTime) {
                    setOpenEditDialog(true);
                }
                 
                  
            }
        } catch (error) {
            console.error('Error fetching downtime threshold:', error);
            if (defaultTime) {
                setOpenEditDialog(true);
            }
        }
    
        setSelectedDeviceId(itemId);
        setTimeValue(defaultTime);
        if (defaultTime) {
            setOpenEditDialog(true);
        }
       
    };

    const handleSaveThreshold = async () => {
        if (!timeValue) return;

        const totalSeconds = timeValue.diff(dayjs().startOf('day'), 'second');
        const did = selectedDeviceId?.id || selectedDeviceId;

        try {
            // 1) downtime threshold (existing path)
            await Downtimeadd('DEVICE', did, 'SERVER_SCOPE', {
                downtime_threasold: totalSeconds,
                lastUpdateTs: Date.now()
            });
            setDeviceThresholds(prev => ({
                ...prev,
                [selectedDeviceId]: totalSeconds
            }));

            // 2) machine_specs (new fields). Cast numerics where set.
            const base = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');
            const specsPayload = {
                manufacturer: editSpecs.manufacturer || '',
                model: editSpecs.model || '',
                year: editSpecs.year || '',
                max_rpm: editSpecs.max_rpm !== '' ? Number(editSpecs.max_rpm) : null,
                spindle_power_kw: editSpecs.spindle_power_kw !== '' ? Number(editSpecs.spindle_power_kw) : null,
                axis_count: editSpecs.axis_count !== '' ? Number(editSpecs.axis_count) : null,
                machine_hour_rate: editSpecs.machine_hour_rate !== '' ? Number(editSpecs.machine_hour_rate) : null,
                // Yantra Intelligence-context fields
                rapid_rate: editSpecs.rapid_rate !== '' ? Number(editSpecs.rapid_rate) : null,
                axis_motor_torque: editSpecs.axis_motor_torque !== '' ? Number(editSpecs.axis_motor_torque) : null,
                spindle_motor_torque: editSpecs.spindle_motor_torque !== '' ? Number(editSpecs.spindle_motor_torque) : null,
                spindle_speed_curve: editSpecs.spindle_speed_curve || '',
            };
            await axios.post(`${base}/api/operator-assignment/machine-specs`, {
                device_id: did, specs: specsPayload,
            });
            // Update local cache for instant UI refresh
            setMachineSpecsMap(prev => ({
                ...prev,
                [did]: { ...(prev[did] || {}), ...specsPayload, image_url: editSpecs.image_url || (prev[did] || {}).image_url },
            }));

            Swal.fire('Success', 'Machine details updated.', 'success');
            getShifts();
            setOpenEditDialog(false);
        } catch (err) {
            console.error('Update error:', err);
            Swal.fire('Error', 'Failed to update downtime.', 'error');
        } finally {
            setOpenEditDialog(false);
        }
    };

    const handleOpenRoboDialog = async (rowData) => {
        const itemId = rowData.id?.$oid || rowData.id;
        setRoboDeviceId(itemId);
        try {
            const response = await Deviceattributeget(itemId.id, 'robo_check');
            const val = response?.[0]?.value;
            setRoboCheckSelected(val === true || val === 'true' ? 'true' : val === false || val === 'false' ? 'false' : '');
        } catch (error) {
            console.error('Error fetching robo_check:', error);
            setRoboCheckSelected('');
        }
        setOpenRoboDialog(true);
    };

    const handleSaveRoboCheck = async () => {
        if (roboCheckSelected === '') return;
        try {
            await Downtimeadd('DEVICE', roboDeviceId.id, 'SERVER_SCOPE', {
                robo_check: roboCheckSelected === 'true',
                lastUpdateTs: Date.now()
            });
            setRoboCheckValues(prev => ({
                ...prev,
                [roboDeviceId.id || roboDeviceId]: roboCheckSelected
            }));
            Swal.fire('Success', 'Robo check updated.', 'success');
            getShifts();
        } catch (err) {
            console.error('Robo check update error:', err);
            Swal.fire('Error', 'Failed to update robo check.', 'error');
        } finally {
            setOpenRoboDialog(false);
        }
    };

    const getShifts = async () => {
        try {
            const { data: allDevices = [] } = await customerbaseddevices(customerId, 100, 0);
            setDevices(allDevices);
            
            const thresholds = {};
            const roboVals = {};
            for (const device of allDevices) {
                try {
                    const response = await Deviceattributeget(device.id.id, 'downtime_threasold');
                    thresholds[device.id.id || device.id] = response?.[0]?.value || null;
                } catch (error) {
                    console.error(`Error fetching threshold for device ${device.id}:`, error);
                    thresholds[device.id.id || device.id] = null;
                }
                try {
                    const roboResp = await Deviceattributeget(device.id.id, 'robo_check');
                    const rv = roboResp?.[0]?.value;
                    roboVals[device.id.id || device.id] = rv !== undefined && rv !== null ? String(rv) : null;
                } catch (error) {
                    roboVals[device.id.id || device.id] = null;
                }
            }
            setDeviceThresholds(thresholds);
            setRoboCheckValues(roboVals);
        } catch (error) {
            console.error('Error fetching devices:', error);
        }
    };

    useEffect(() => {
        getShifts();
    }, []);

    const base = (window._env_?.SERVER_URL2 || '').replace(/\/$/, '');

    return (
        <div className="pages">
            <div className="pagecontents">
                <div className="left-labels">
                    <div className="shift-content">
                        <h5>Machines List</h5>
                    </div>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: 16, padding: '0 16px 16px',
                }}>
                    {devices?.length > 0 ? (
                        [...new Map(devices.map(device => 
                            [device.id?.$oid || device.id, device])).values()]
                            .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                            .map((item) => {
                                const itemId = item.id?.$oid || item.id;
                                const threshold = deviceThresholds[item.id.id || item.id] ?? 'Not set';

                                const did = itemId?.id || itemId;
                                const specs = machineSpecsMap[did] || {};

                                return (
                                    <div key={did} style={{
                                        background: 'var(--y-bg, #fff)', border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 10,
                                        overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                        display: 'flex', flexDirection: 'column',
                                        position: 'relative',
                                    }}>
                                        {/* Top-right floating Edit button */}
                                        <button
                                            onClick={(e) => { e.stopPropagation(); handleOpenEditDialog(item); }}
                                            title="Edit machine details"
                                            style={{
                                                position: 'absolute', top: 8, right: 8, zIndex: 2,
                                                background: 'rgba(255,255,255,0.95)', border: '1px solid #f59e0b',
                                                color: '#b45309', borderRadius: 6,
                                                padding: '4px 10px', fontSize: 12, fontWeight: 700,
                                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                                cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
                                            }}
                                        >
                                            ✎ Edit
                                        </button>
                                        <div style={{
                                            width: '100%', height: 160, background: 'var(--y-bg, #f3f4f6)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            overflow: 'hidden',
                                        }}>
                                            <MachineImage src={specs.image_url ? `${base}${specs.image_url}` : ''} alt={item.name} />
                                        </div>
                                        <div style={{ padding: '12px 14px', flex: 1 }}>
                                            <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 4 }}>
                                                {item.name}
                                            </div>
                                            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                                                {specs.manufacturer || '—'}{specs.model ? ' · ' + specs.model : ''}{specs.year ? ' · ' + specs.year : ''}
                                            </div>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                                                {specs.max_rpm ? (<span style={{ fontSize: 11, padding: '2px 8px', background: '#eff6ff', color: '#1e40af', borderRadius: 4 }}>{specs.max_rpm} RPM</span>) : null}
                                                {specs.spindle_power_kw ? (<span style={{ fontSize: 11, padding: '2px 8px', background: '#ecfccb', color: '#3f6212', borderRadius: 4 }}>{specs.spindle_power_kw} kW</span>) : null}
                                                {specs.axis_count ? (<span style={{ fontSize: 11, padding: '2px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 4 }}>{specs.axis_count}-axis</span>) : null}
                                                {specs.machine_hour_rate ? (<span style={{ fontSize: 11, padding: '2px 8px', background: '#dcfce7', color: '#065f46', borderRadius: 4, fontWeight: 700 }}>₹ {Number(specs.machine_hour_rate).toLocaleString('en-IN')}/hr</span>) : null}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#374151', marginBottom: 10 }}>
                                                <span style={{ fontWeight: 600 }}>Downtime trigger:</span>
                                                <span>{formatSecondsToTime(threshold)}</span>
                                            </div>
                                            {showRoboCheck && (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderRadius: '4px', background: '#80808040' }}>
                                                    <span style={{fontWeight: 600, fontSize: '20px'}}>Robot Machine:</span>
                                                    <FormControlLabel
                                                        control={
                                                            <Switch
                                                                checked={roboCheckValues[item.id.id || item.id] === 'true' || roboCheckValues[item.id.id || item.id] === true}
                                                                onChange={async (e) => {
                                                                    const newVal = e.target.checked ? 'true' : 'false';
                                                                    const itemId = item.id?.$oid || item.id;
                                                                    try {
                                                                        await Downtimeadd('DEVICE', itemId.id, 'SERVER_SCOPE', {
                                                                            robo_check: e.target.checked,
                                                                            lastUpdateTs: Date.now()
                                                                        });
                                                                        setRoboCheckValues(prev => ({ ...prev, [item.id.id || item.id]: newVal }));
                                                                    } catch (err) {
                                                                        console.error('Robo check update error:', err);
                                                                    }
                                                                }}
                                                                sx={{
                                                                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#ff9800' },
                                                                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#ff9800' },
                                                                }}
                                                            />
                                                        }
                                                        label=""
                                                        sx={{ margin: 0 }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })
                    ) : (
                        <div style={{ margin: '2rem', color: '#888' }}>No devices found.</div>
                    )}
                </div>

                <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
                    <DialogTitle>Edit Machine Details</DialogTitle>
                    <DialogContent>
                    {/* Image upload */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16, padding: 12, border: '1px solid var(--y-border, #e5e7eb)', borderRadius: 8, background: 'var(--y-bg-soft, #fafafa)' }}>
                        {editSpecs.image_url ? (
                            <img src={`${base}${editSpecs.image_url}`} alt="machine"
                                 style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 6, border: '1px solid var(--y-border, #d1d5db)' }} />
                        ) : (
                            <div style={{ width: 90, height: 90, background: 'var(--y-bg, #f3f4f6)', border: '1px dashed #d1d5db', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 28 }}>+</div>
                        )}
                        <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 2 }}>Machine Photo</label>
                            <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 6 }}>JPG / PNG / WEBP, max 500 KB</div>
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={async (e) => {
                                    const file = e.target.files?.[0];
                                    if (!file) return;
                                    if (file.size > 500 * 1024) { Swal.fire({ icon: 'error', title: 'Too large', text: 'Max 500 KB' }); return; }
                                    const did = selectedDeviceId?.id || selectedDeviceId;
                                    try {
                                        const fd = new FormData();
                                        fd.append('customer_id', cleanCustomerId(customerId));
                                        fd.append('device_id', String(did));
                                        fd.append('image', file);
                                        const res = await fetch(`${base}/api/operator-assignment/machine-image`, { method: 'POST', body: fd });
                                        const j = await res.json();
                                        if (j.url) setEditSpecs(prev => ({ ...prev, image_url: j.url }));
                                        else Swal.fire({ icon: 'error', title: 'Upload failed', text: j.message || 'Try again' });
                                    } catch (err) { Swal.fire({ icon: 'error', title: 'Upload error', text: err.message }); }
                                }}
                            />
                        </div>
                    </div>

                    {/* Manufacturer / Model / Year */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
                        <TextField size="small" label="Manufacturer" value={editSpecs.manufacturer}
                            onChange={(e) => setEditSpecs(prev => ({ ...prev, manufacturer: e.target.value }))} fullWidth />
                        <TextField size="small" label="Model" value={editSpecs.model}
                            onChange={(e) => setEditSpecs(prev => ({ ...prev, model: e.target.value }))} fullWidth />
                        <TextField size="small" label="Year" value={editSpecs.year}
                            onChange={(e) => setEditSpecs(prev => ({ ...prev, year: e.target.value }))} fullWidth />
                    </div>

                    {/* Max RPM / Spindle Power / Axis */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 16 }}>
                        <TextField size="small" type="number" label="Max RPM" value={editSpecs.max_rpm}
                            onChange={(e) => setEditSpecs(prev => ({ ...prev, max_rpm: e.target.value }))} fullWidth inputProps={{ min: 0 }} />
                        <TextField size="small" type="number" label="Spindle Power (kW)" value={editSpecs.spindle_power_kw}
                            onChange={(e) => setEditSpecs(prev => ({ ...prev, spindle_power_kw: e.target.value }))} fullWidth inputProps={{ min: 0, step: '0.1' }} />
                        <TextField size="small" type="number" label="Axis Count" value={editSpecs.axis_count}
                            onChange={(e) => setEditSpecs(prev => ({ ...prev, axis_count: e.target.value }))} fullWidth inputProps={{ min: 0 }} />
                        <TextField size="small" type="number" label="Hour Rate (₹/hr)" value={editSpecs.machine_hour_rate}
                            onChange={(e) => setEditSpecs(prev => ({ ...prev, machine_hour_rate: e.target.value }))} fullWidth inputProps={{ min: 0, step: '0.01' }} />
                    </div>

                    {/* Yantra Intelligence Context — feeds machine constraints into the optimizer so
                        suggestions stay within physical machine limits */}
                    <div style={{ border: '1px solid #c4b5fd', background: '#faf5ff', borderRadius: 8, padding: 12, marginBottom: 16 }}>
                        <div style={{ fontSize: 12, fontWeight: 800, color: '#5b21b6', marginBottom: 8, letterSpacing: 0.4, textTransform: 'uppercase' }}>
                            🤖 Yantra Intelligence Context (machine limits)
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 10 }}>
                            <TextField size="small" type="number" label="Rapid Rate (mm/min)" value={editSpecs.rapid_rate}
                                onChange={(e) => setEditSpecs(prev => ({ ...prev, rapid_rate: e.target.value }))}
                                fullWidth inputProps={{ min: 0 }}
                                helperText="e.g. 48000" />
                            <TextField size="small" type="number" label="Axis Motor Torque (Nm)" value={editSpecs.axis_motor_torque}
                                onChange={(e) => setEditSpecs(prev => ({ ...prev, axis_motor_torque: e.target.value }))}
                                fullWidth inputProps={{ min: 0, step: '0.1' }}
                                helperText="e.g. 45" />
                            <TextField size="small" type="number" label="Spindle Motor Torque (Nm)" value={editSpecs.spindle_motor_torque}
                                onChange={(e) => setEditSpecs(prev => ({ ...prev, spindle_motor_torque: e.target.value }))}
                                fullWidth inputProps={{ min: 0, step: '0.1' }}
                                helperText="e.g. 120" />
                        </div>
                        <TextField size="small" multiline minRows={2} fullWidth
                            label="Spindle Speed / Torque Curve"
                            placeholder='e.g. "Constant torque to 4500 RPM, ~30% drop by 8000 RPM"'
                            value={editSpecs.spindle_speed_curve}
                            onChange={(e) => setEditSpecs(prev => ({ ...prev, spindle_speed_curve: e.target.value }))}
                            helperText="Free text — describes RPM vs torque behavior so Yantra Intelligence knows safe spindle limits" />
                    </div>

                    {/* Downtime trigger (existing) */}
                    <div className={`form_field time_field_bg`}>
                  <DemoItem label="Downtime Duration *" className="white-label">
                    <LocalizationProvider
                     dateAdapter={AdapterDayjs}>
                      <TimePicker                    
                        value={timeValue}
                        onChange={(newValue) => setTimeValue(newValue)}
                        views={['hours', 'minutes', 'seconds']}
                        openTo="hours"
                        format="HH:mm:ss"
                        ampm={false}
                        InputLabelProps={{ required: true }}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'black',
                            },
                            '&:hover fieldset': {
                              borderColor: 'black',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: 'orange',
                            },
                            '& .MuiOutlinedInput-input': {
                              color: 'black',
                            },
                            '&.Mui-focused .MuiOutlinedInput-input': {
                              caretColor: 'orange',
                            },
                            '&::placeholder': {
                              color: 'black',
                              opacity: 1,
                            },
                          },
                        }}
                      />
                    </LocalizationProvider>
                  </DemoItem>                  
                </div>
                    </DialogContent>
                    <DialogActions>
                        <Button type="submit" variant="contained" className="filter_btn btn_orange" sx={{ backgroundColor: '#ff4444' }} onClick={() => setOpenEditDialog(false)}>Cancel</Button>
                        <Button type="submit" variant="contained" className="filter_btn btn_orange" sx={{ backgroundColor: '#ff9800' }} onClick={handleSaveThreshold}>Save</Button>
                    </DialogActions>
                </Dialog>
            </div>
        </div>
    );
};

export default MachineList;